<template>
  <div>
  <p-form ref="invoiceForm" :id='formId'>
    <b-card no-body class="mb-1">
      <b-row cols="1" cols-sm="3" cols-xl="5" class="mt-3">
        <b-col>
          <div>
            <p-input
              v-model="invoice.status"
              :model-value="invoice.status"
              label="Status"
              rule="required"
              :key="invoice.status"
              :disabled="true"
            >
              <template v-slot:append>
                <div>
                  <p-button
                    variant="btn btn-primary"
                    class="ms-2"
                    :is-busy="isBusyInvoice"
                    v-if="invoice.status == 'Open' && !isNew"
                    @click="handleCancelInvoice()"
                  >Cancel
                  </p-button>
                  <p-button
                    variant="btn btn-primary"
                    class="ms-2"
                    :is-busy="isBusyInvoice"
                    v-if="invoice.status == 'Cancelled'"
                    @click="handleReopenInvoice()"
                  >Reopen
                  </p-button>                
                  <p-button
                    variant="btn btn-primary"
                    class="ms-2"
                    :is-busy="isBusyInvoice"
                    v-if="isPosted"
                    @click="handleUnpostInvoice()"
                  >Unpost
                  </p-button>
                  <p-button
                    variant="btn btn-primary"
                    class="ms-2"
                    :is-busy="isBusyInvoice"
                    v-if="isPosted"
                    @click="handlePayInvoice()"
                  >Set to Paid
                  </p-button>
                </div>
              </template>
            </p-input>
          </div>
        </b-col>        
        <b-col>
            <p-input label="Invoice Number"
                      v-model="invoice.invoiceNumber"
                      rules="required|max:100"
                      :disabled="disableEdit"
                      @change="checkForExistingInvoices()"/>
        </b-col>
        <b-col>
          <p-datepicker
            label="Invoice Date"
            rules="required"
            @date-picker-initialized="e => {$emit('date-picker-initialized');}"
            v-model="invoice.invoiceDate"
            :disabled="disableEdit"
          />
        </b-col>
        <b-col>
            <p-datepicker label="Entry Date"
              v-model="invoice.entryDate"
              @date-picker-initialized="e => {$emit('date-picker-initialized');}"
              :disabled="disableEdit"/>
        </b-col>         
        <b-col>
            <p-datepicker
              label="Due Date"
              rules="required"
              @date-picker-initialized="e => {$emit('date-picker-initialized');}"
              v-model="invoice.dueDate"
              :disabled="disableEdit"
            />
        </b-col>   
        <b-col>
            <p-number label="Terms %"
                      v-model="invoice.termPercent"
                      input-type="percent"
                      rules="max_value:100|p-decimal:5,2|min_value:0"
                      :disabled="disableEdit"/>
        </b-col>
        <b-col>
            <p-number label="Days #"
                      input-type="integer"
                      v-model="invoice.termDays"
                      rules="p-integer|min_value:0"
                      :disabled="disableEdit"/>
        </b-col>
        <b-col>
            <p-advanced-select label="Supplier"
                                data-type="suppliers"
                                v-model="invoice.supplierId"
                                rules="required"
                                :disabled="disableEdit"
                                @change="checkForExistingInvoices()"/>
        </b-col>
        <b-col>
            <p-datepicker label="Return Expiration Date"
                          v-model="invoice.returnExpirationDate"
                          @date-picker-initialized="e => {$emit('date-picker-initialized');}"
                          :disabled="disableEdit"/>
        </b-col>
        <b-col>
            <p-select v-if="currencyEnabled"
                      label="Currency"
                      v-model="invoice.currency"
                      :options="currencyOptions"
                      rules="required"/>
            <p-input v-else
                     label="Currency"
                     v-model="invoice.currency"
                     :disabled="true"/>
        </b-col>
        <b-col v-if="showTaxTotals">
            <p-number label="Total GST"
                      input-type="money"
                      v-model="invoice.totalGst"
                      rules="min_value:0"
                      :disabled="disableEdit"/>
        </b-col>
        <b-col v-if="showTaxTotals">
            <p-number label="Total PST"
                      input-type="money"
                      v-model="invoice.totalPst"
                      rules="min_value:0"
                      :disabled="disableEdit"/>
        </b-col>
        <b-col v-if="!enableExpenses">
            <p-number label="Adjustments"
                      input-type="money"
                      v-model="invoice.adjustmentsTotal"
                      :disabled="true">
                <template v-slot:append>
                  <b-button
                    @click="showInvoiceAdjustmentsModal"
                    style="white-space:nowrap;"
                    >{{ invoice.status == 'Open' ? 'Edit' : 'View' }}</b-button>
                </template>
              </p-number>
        </b-col>
        <b-col>
            <p-input label="Invoice Total"
                     :model-value="invoiceTotal"
                     :disabled="true"/>
        </b-col>        
      </b-row>
      <b-row class="mt-5">
        <b-col cols="auto" class="me-auto mb-2">
          <AddProducts
            v-model:includeFullyInvoicedProducts="includeFullyInvoicedProducts"
            :items="addProductOptions"
            :disable-add-button="disableEdit"
            :existing-invoice-details="invoice.invoiceDetails"
            :vendor-id="invoice.supplierId"
            :invoice-id="invoice.invoiceId"
            :isExpenses= enableExpenses
            @selections="addProducts" 
            :disabled="isPosted">
          </AddProducts>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <p-table v-if="!enableExpenses" :items="invoice.invoiceDetails"
                    :enable-row-selection="tableActions.length > 0"
                    :selection-actions="tableActions"
                    :fields="tableDefinition.fields"
                    sort-by="authorTitleOrDescription"
                    >
            <template v-slot:custom-foot="data">
              <b-tr>
                <!-- this empty footer gets rid of an unecessary vertical scrollbar -->
                <b-th colspan="20"><br/></b-th>
              </b-tr>
            </template>
            <template v-slot:cell(purchaseOrder)="{ value, item }">
              <router-link v-if="item.purchaseOrderId" :to="`/purchaseorders/${item.purchaseOrderId}`">
                  <span style="white-space: nowrap"> {{item.purchaseOrder}}</span>
              </router-link>
              <template v-else>
                <span style="white-space: nowrap">{{item.purchaseOrder}}</span>
              </template>
            </template>
            <template v-slot:cell(price)="{ value, item }">
                <p-number v-model="item.price"
                          name="price"
                          :disabled="disableEdit"
                          compact-format
                          @change="calculateCost(item)"
                          rules="min_value:0"
                          input-type="money"/>
            </template>
            <template v-slot:cell(discount)="{ value, item }">
                <p-number v-model="item.discount"
                          name="discount"
                          :disabled="disableEdit"
                          compact-format
                          rules="min_value:0|max_value:100"
                          @change="calculateCost(item)"
                          input-type="percent"/>
            </template>
            <template v-slot:cell(cost)="{ value, item }">
                <p-number v-model="item.cost"
                          name="cost"
                          :disabled="disableEdit"
                          compact-format
                          rules="required:min_value:0"
                          @change="calculatePriceAndDiscount(item)"
                          input-type="money"/>
            </template>
            <template v-slot:cell(margin)="{ value, item }">
                <p-number v-model="item.margin"
                          name="margin"
                          :disabled="disableEdit"
                          compact-format
                          text-only
                          input-type="percent"/>
            </template>
            <template v-slot:cell(retail)="{ value, item }">
                <p-number v-model="item.retail"
                          :disabled="disableEdit"
                          name="retail"
                          compact-format
                          rules='required'
                          text-only
                          input-type="money"/>
            </template>
            <template v-slot:cell(totalPurchaseOrderReceivedQty)="{ value, item }">
                <p-number v-model="item.totalPurchaseOrderReceivedQty"
                          name="TotalReceivedQtY"
                          text-only
                          compact-format
                          input-type="integer"
                          :disabled="disableEdit"/>
            </template>
            <template v-slot:cell(qty)="{ value, item }">
                <p-number v-model="item.qty"
                          name="Invoice Qty"
                          input-type="integer"
                           rules="required|min_value:1"
                           @change="calculateExtCost(item)"
                          compact-format
                          :disabled="disableEdit"/>
            </template>
            <template v-slot:cell(extendedCost)="{ value, item }">  
                <p-number 
                       v-model="item.extendedCost"
                          name="Ext Cost"
                          input-type="money"
                          rules="required|min_value:0"
                          compact-format 
                          :disabled="disableEdit" 
                         /> 
                         
            </template>
              <template v-slot:head(returnQty)="data">
                {{'\u200B'}}
                <div v-if="hasAnyReturns">Return Qty</div>
              </template>
              <template v-slot:cell(returnQty)="{ value, item }">
                <div v-if="hasAnyReturns">
                    <b-dropdown v-if="item.creditRequestDetails.length > 0"
                        :text="integerFormatter(item.creditRequestDetails.reduce((a, i) => a + i.returnQty, 0))"
                        size="xs"
                        variant="light">

                    <b-dropdown-item
                                    v-for="creditRequestDetail in item.creditRequestDetails"
                                    :key="creditRequestDetail.creditRequestId"
                                    @click="loadCreditRequest(creditRequestDetail.creditRequestId)">
                        {{ creditRequestDetail.requestNumber  }}
                    </b-dropdown-item>
                  </b-dropdown>
                  <div class="pe-3" v-else>
                    0
                  </div>
                </div>
              </template>
            <template v-slot:cell(removeDetail)="{ value, item }">
                <b-button class="p-1 ms-1 short-button" :disabled="disableEdit" @click="!disableEdit && confirmDeleteDetail(item)">
                    <BIconTrash :disabled="disableEdit" font-scale="0.9" class="mb-3" icon="trash" ></BIconTrash>
                </b-button>
            </template>
          </p-table>
          

          <p-table v-if="enableExpenses" :items="invoice.invoiceMiscExpensesDetails"
                    :enable-row-selection="tableActions.length > 0"
                    :selection-actions="tableActions"
                    :fields="tableExpDefinition.fields"
                    sort-by="ledgerNumber"
                    >
            <template v-slot:custom-foot="data">
              <b-tr>
                <!-- this empty footer gets rid of an unecessary vertical scrollbar -->
                <b-th colspan="20"><br/></b-th>
              </b-tr>
            </template>     
            <template v-slot:cell(description)="{ value, item }">
              <p-input v-model="item.description"
                      rules="max:500"
                      compact-format
                      :disabled="disableEdit"/>
            </template>                 
            <template v-slot:cell(cost)="{ value, item }">
                <p-number v-model="item.cost"
                          name="cost"
                          :disabled="disableEdit"
                          compact-format
                          rules="required|min_value:1"
                           @change="calculateExtCost(item)"
                          input-type="money"/>
            </template>
            <template v-slot:cell(qty)="{ value, item }">
                <p-number v-model="item.qty"
                          name="Invoice Qty"
                          input-type="integer"
                           rules="required|min_value:1"
                           @change="calculateExtCost(item)"
                          compact-format
                          :disabled="disableEdit"/>
            </template>
            <template v-slot:cell(extendedCost)="{ value, item }">  
                <p-number 
                       v-model="item.extendedCost"
                          name="Ext Cost"
                          input-type="money"
                          rules="required|min_value:0"
                          compact-format 
                          :disabled="disableEdit" 
                         /> 
                         
            </template>              
            <template v-slot:cell(removeDetail)="{ value, item }">
                <b-button class="p-1 ms-1 short-button" :disabled="disableEdit" @click="!disableEdit && confirmDeleteDetail(item)">
                    <BIconTrash :disabled="disableEdit" font-scale="0.9" class="mb-3" icon="trash" ></BIconTrash>
                </b-button>
            </template>
          </p-table>
        </b-col>
      </b-row>
    </b-card>
    <InvoiceAdjustmentsModal  
      name="invoiceAdjustmentsModal"
      :is-busy="this.isBusyInvoice"
      :adjustments-data="invoice.invoiceAdjustments"
      @ok="handleSaveAdjustments"
      :can-modify="invoice.status == 'Open' && invoice.invoiceDetails.length > 0"
      :location-id="computedLocationId"
    />
  </p-form>
  </div>
</template>

<script>
import unionBy from "lodash/unionBy";
import { currencyFormatter, integerFormatter } from "../../components/Common/Formatters.js";
// BIconTrash - use <i class="bi bi-trash"> or font-awesome icon instead
import pricing from "@/services/PricingCalculations.js"
import axios from "axios";
import AddProducts from '@/views/Invoices/AddProductsExpensesModal.vue';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import InvoiceAdjustmentsModal from '@/views/Invoices/AdjustmentsModal.vue';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';

export default {
  name: "Invoice",
  props: {
    invoice: [Object],
    isBusy: {
      type: Boolean,
      default: false,
    },
    purchaseOrderDetails: {
      type: Array,
      default: () => [],
      },
    allRequestDetails: [Array]
  },
  components: {
    AddProducts,
    InvoiceAdjustmentsModal
  },
  created: function() {
      this.isBusyInvoice = this.isBusy;
  },
  data() {
        return {
        parameters: {},
        locationId: 0,
        currencyOptions: ['USD', 'CAD'],
        includeFullyInvoicedProducts: false,
        isBusyInvoice: {
          type: Boolean,
          default: false,
        },
        tableDefinition: {
          fields: [
            {
              key: "sku",
              label: "SKU",
              sortable: true,
              automaticSearch: true,
            },
            {
              key: "purchaseOrder",
              sortable: true
            },
            {
                key: "termName",
                label: "Term",
                sortable: true
            },
            {
              key: "authorTitleOrDescription",
              label:"Author/Title or Description",
              sortable: true,
            },
            {
              label: 'Author',
              key: 'author',
              sortable: true,
              automaticSearch: true,
              hidden:true
            },
            {
              label: 'Title',
              key: 'title',
              sortable: true,
              automaticSearch: true,
              hidden:true
            },
            {
              label: 'Description',
              key: 'authorTitleOrDescription',
              sortable: true,
              automaticSearch: true,
              hidden:true
            },
            {
              key: "copyright",
              label: 'Variant 1',
            },
            {
              key: "edition",
              label: 'Variant 2',
            },
            {
              key: 'isbnCatalog',
              sortable: true,
              label: 'Catalog # / ISBN',
              tdClass: 'noWrap'
            },
            {
              key: 'catalogNumber',
              label: 'Catalog #',
              automaticSearch: true,
              hidden:true
            },
            {
              key: 'isbn',
              label: 'ISBN',
              automaticSearch: true,
              hidden:true
            },
            {
              key: 'productType',
              label: 'Product Type',
            },
            {
              key: "barcodes",
              label:'Barcode',
              hidden:true,
              automaticSearch: true,
            },
            {
              key: "price",
              thClass: 'text-center'
            },
            {
              key: "discount",
              thClass: 'text-center'
            },
            {
              key: "cost",
              label: "Invoice Cost *",
              thClass: 'text-center'
            },
            {
              key: "margin",
              label: "Margin",
              thClass: 'text-center'
            },
            {
              key: "retail",
              label: "Retail",
              thClass: 'text-center'
            },
            {
                key: 'totalPurchaseOrderReceivedQty',
                label: 'Total Rcvd Qty',
                thClass: 'text-center'
            },
            {
                key: 'qty',
                label: 'Invoice Qty *',
                thClass: 'text-center'
            },
            {
                key: 'extendedCost',
                label: 'Ext Cost *',
                formatter:currencyFormatter,
                thClass: 'text-center',
                tdClass: 'text-end'
            },
            {
                key: 'returnQty',
                label: 'Return Qty',
                thClass: 'text-end',
                tdClass: 'text-end'
            },
            {
                key: 'removeDetail',
                label: ''
            }
          ]
        },
        tableExpDefinition: {
          fields: [
            {
              key: "ledgerNumber",
              label: "Ledger Number",
              sortable: true,
              automaticSearch: true,
            },
            {
                key: "ledgerType",
                label: "Type",
                sortable: true
            },
            {
              key: "ledgerCategory",
              label:"Category",
              sortable: true,
            },
            {
              label: 'Credit/Debit',
              key: 'isDebit',
              sortable: true,
              automaticSearch: true
            },
            {
              label: 'Description',
              key: 'description',
              sortable: true,
              automaticSearch: true
            },
            {
              key: "cost",
              label: "Invoice Cost *",
              formatter:currencyFormatter,
              thClass: 'text-start',
              tdClass: 'text-end'
            },
            {
                key: 'qty',
                label: 'Invoice Qty *',
                thClass: 'text-start',
                tdClass: 'text-end'
            },
            {
                key: 'extendedCost', 
                label: 'Ext Cost *',
                formatter:currencyFormatter, 
                thClass: 'text-start',
                tdClass: 'text-end'
            },
            {
                key: 'removeDetail',
                tdClass: 'text-center',
                label: ''
            }
          ]
        }
      };          
    },
    watch: {
        invoice: {
            immediate: true,
            handler: function(newInvoice) {
                if (!newInvoice || newInvoice.currency) return;
                // Invoices locked before this release were never saved with a currency —
                // leave those blank rather than stamping USD on a record that can no longer
                // be changed. Everything still editable defaults to USD.
                if (!this.currencyEnabled) return;
                newInvoice.currency = 'USD';
            }
        },
        'invoice.currency': function(newCurrency) {
            // GST and PST only apply to CAD invoices. Drop the amounts on the way out
            // so the total updates immediately; the save deletes the adjustment rows.
            if (newCurrency === 'CAD') return;
            this.invoice.totalGst = null;
            this.invoice.totalPst = null;
        },
        purchaseOrderDetails: function() {
            this.syncReceivedQty();
        },
        'invoice.status': async function(newVal) {
              if (newVal && newVal === 'Paid') {
                if (!(await this.$refs.invoiceForm.validate())) {
                    this.invoice.status = this.invoice.previousStatus;
                    this.$appError('Please resolve all validation errors on this invoice before marking it as paid.');
                }
                else {
                  this.invoice.isPaid = true;
                }
            }              
        },              
        isBusy: {
            handler: function(newVal) {
                this.isBusyInvoice = newVal;
            }
        }
    },
    computed: {
      isPosted() {
        return this.invoice && this.invoice.status == 'Posted';
      },
      hasAnyReturns(){
        return this.invoice.invoiceDetails.some(x => x.creditRequestDetails?.length > 0 );
      },
      tableActions() {
          if (this.disableEdit) {
              return [];
          }
          return [
              {
                  label: 'Delete',
                  action: this.confirmDeleteDetails
              }
          ];
        },
        disableEdit() {
            return this.invoice.isPaid || this.invoice.status !== 'Open';
        },
        // The currency can only be changed while the invoice is still open. Posted,
        // Paid and Cancelled invoices show it as plain text instead of a dropdown.
        currencyEnabled() {
            return !this.invoice.isPaid && !['Posted', 'Paid', 'Cancelled'].includes(this.invoice.status);
        },
        // GST and PST are Canadian taxes, and expense invoices carry no adjustments at
        // all — the server refuses to sync tax for them, so the fields must not appear.
        showTaxTotals() {
            return this.invoice.currency === 'CAD' && !this.enableExpenses;
        },
        enableExpenses() {
            return this.$route?.params?.id === 'newexp'
                || this.invoice?.isExpenses
                || (this.invoice?.invoiceMiscExpensesDetails?.length ?? 0) > 0;
        },
        addProductOptions() {
            return this.purchaseOrderDetails.filter(
                i =>
                    (this.includeFullyInvoicedProducts ||
                        i.qty - i.totalInvoicedQty > 0) &&
                    !this.invoice.invoiceDetails.some(
                        id => id.sku === i.sku && i.termId === id.termId
                    )
            );
        },
        invoiceTotal() {
          
            let total = this.invoice.invoiceDetails.reduce(
                (a, i) => a + i.extendedCost,
                0
            );
            
            total += this.invoice.invoiceMiscExpensesDetails.reduce(
                (a, i) => a + i.extendedCost,
                0
            );

            // adjustmentsTotal deliberately excludes GST and PST so the header reads
            // without overlap, so the tax amounts are added back here.
            var invoiceTotal = total
                + (this.invoice.adjustmentsTotal ?? 0)
                + (this.invoice.totalGst ?? 0)
                + (this.invoice.totalPst ?? 0);

            if (isNaN(invoiceTotal)) return currencyFormatter(0);

            return currencyFormatter(invoiceTotal);
        },
        formId() {
            return `invoice-${this.invoice.invoiceNumber}`;
        },
        isNew() {
          return !this.invoice.invoiceId;
        },
        disableSave() {
          return this.invoice.status != 'Open';
        },
        computedLocationId() {
          return this.invoice.invoiceDetails[0]?.locationId;
        }
    },
    mounted: async function() {
      axios.get('products/gmparameters').then(resp => {
          this.parameters = resp.data;
      });
      this.syncReceivedQty();
      this.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

  },
  methods: {
    integerFormatter: integerFormatter,  
    calculateExtCost: function(invoiceDetail) {
        // qty * cost can produce floating-point garbage (e.g. 60 * 43.94 = 2636.3999999999996),
        // which then trips the money input's decimal-places validation and shows the long
        // value when the user focuses the field. Round to cents.
        const raw = (invoiceDetail.qty || 0) * (invoiceDetail.cost || 0);
        invoiceDetail.extendedCost = Math.round(raw * 100) / 100;
    },
    loadCreditRequest(id) {
        this.$router.push(
            `/creditrequests/${id}`
        )
    },
    syncReceivedQty(){
      this.invoice.invoiceDetails.forEach((x) => {
        let pod = this.purchaseOrderDetails.find((p) => p.sku === x.sku && x.termId === p.termId && p.purchaseOrderId === x.purchaseOrderId);
        if(pod){
          x.totalPurchaseOrderReceivedQty = pod.receivedQty;
        }
      });
    },
    calculatePriceAndDiscount: function(invoiceDetail) {
      if(invoiceDetail.discount == null && invoiceDetail.price != null){
        invoiceDetail.price = invoiceDetail.cost;
        this.calculateExtCost(invoiceDetail);
        return;
      }
      invoiceDetail.discount = pricing.discountFromPriceAndCost(invoiceDetail.price, invoiceDetail.cost ?? 0);
      this.calculateExtCost(invoiceDetail);
    },
    calculateCost: function(invoiceDetail) {
      if(invoiceDetail.price == null) { return;}
      invoiceDetail.cost = pricing.costFromPriceAndDiscount(invoiceDetail.price, invoiceDetail.discount ?? 0); 
      this.calculateExtCost(invoiceDetail);
    },
    addProducts(items) {
      if (!this.enableExpenses)
      {
        items = items.map((x) => {
          const qty = (x.totalPurchaseOrderReceivedQty || x.receivedQty) - x.totalInvoicedQty;
          return {
            ...x,
            //purchaseOrderDetail is used by credit requests, can probably be removed when that section is reworked to work without POs
            purchaseOrderDetail: x,
            //newly added products don't have returns yet
            creditRequestDetails: [],
            totalPurchaseOrderReceivedQty: x.totalPurchaseOrderReceivedQty || x.receivedQty,
            qty: qty,
            // Round at the source so the stored value is what the user sees in the
            // formatted display ($9,499.20), not 9499.199999999999 underneath.
            extendedCost: Math.round((qty * x.cost) * 100) / 100,
            locationId: x.locationId
          };
        });

        this.invoice.invoiceDetails = unionBy(
          items,
          this.invoice.invoiceDetails,
          (x) => x.sku + '-' + (x.termId ?? 0) + '-' + x.purchaseOrderId
        );
      }
      else
      {
        items = items.map(x => ({
          ledgerNumber: x.ledgerNumber,
          ledgerCategory: x.category,
          ledgerType: x.chartOfAccountsType,
          isDebit: x.debitOrCredit,
          description: x.description,
          qty:1,
          cost:0,
          extendedCost:0,
          locationId: this.locationId
        }));

        this.invoice.invoiceMiscExpensesDetails = unionBy(
          items,
          this.invoice.invoiceMiscExpensesDetails,
          (x) => x.ledgerNumber
        );  
      }      
      this.$refs.invoiceForm.reset();
    },
    confirmDeleteDetails(selectedRows) {
      var detailsWithReturns = selectedRows.filter(x => this.invoiceDetailHasReturn(x));

      if(detailsWithReturns.length > 0){
        this.$appError(
                    `Unable to delete selected product(s) because the following have returns attatched:\n
                    ${detailsWithReturns.map(x => `SKU: ${x.sku}, PO: ${x.purchaseOrder}, Term: ${x.termName} `).join('\n')}`
                )
            return;
      }
      this.$bvModal
          .msgBoxConfirm(
              'Are you sure you want to remove the selected product(s) from the invoice?'
          )
          .then(value => {
              if (value) {
                  selectedRows.forEach(x => {
                      this.deleteDetail(x);
                  });
              }
          });
    },
    invoiceDetailHasReturn(invoiceDetail){
      if(invoiceDetail.creditRequestDetails && invoiceDetail.creditRequestDetails.length > 0){
        return true;
      }
      return this.allRequestDetails && this.allRequestDetails.some(
            p => p.sku === invoiceDetail.sku && p.termId === invoiceDetail.termId
          );
    },
    confirmDeleteDetail(detail) {
        let hasReturnDetail = this.invoiceDetailHasReturn(detail);
        if (hasReturnDetail) {
            this.$appError(
                    'This product cannot be deleted because it has returns attached to it.'
                )
            return;
        } else {
            this.$bvModal
                .msgBoxConfirm(
                    'Are you sure you want to remove this product from the invoice?'
                )
                .then(value => {
                    if (value) {
                        this.deleteDetail(detail);
                    }
                });
        }
    },
    deleteDetail(detail) {

      if(this.enableExpenses)
    {
      let index = this.invoice.invoiceMiscExpensesDetails.indexOf(detail);
        if (index !== -1) {
            this.invoice.invoiceMiscExpensesDetails.splice(index, 1);
        }
    }
    else
    {
        let index = this.invoice.invoiceDetails.indexOf(detail);
        if (index !== -1) {
            this.invoice.invoiceDetails.splice(index, 1);
        }
      }
    },
    exportInvoice() {
        this.isBusyInvoice = true;
        axios
            .get('invoices/exportInvoice', {
                params: { invoiceId: this.invoice.invoiceId },
                responseType: 'blob'
            })
            .then(result => {
                downloadFileToBrowser(
                    result.data,
                    'Invoice',
                    'application/pdf'
                );
                this.$appSuccess(`Invoice generated successfully.`)
            })
            .finally(() => {
                this.isBusyInvoice = false;
            });
    },
    checkForExistingInvoices() {
        if (this.isNew && this.invoice.invoiceNumber && this.invoice.supplierId) {
          axios.post('invoices/search', {
              automatic: `${this.invoice.invoiceNumber}|${this.invoice.supplierId}`,
              searchBy: 'Invoice #|Supplier ID'
          }).then((result) => {
            if (result?.data?.count > 0) {
              this.$appWarning(`This Invoice number already exists for the supplier. Please return to search if you wish to edit the existing record.`);
            }
          });
        }
    },
    handleCancelInvoice() {
      this.$emit('cancel-invoice');
    },
    handleReopenInvoice() {
      this.$emit('reopen-invoice');
    },
    handleUnpostInvoice() {
      this.$emit('unpost-invoice');
    },
    handlePayInvoice() {
      this.$emit('pay-invoice');
    },
    showInvoiceAdjustmentsModal() {
      this.$bvModal.show('invoiceAdjustmentsModal');
    },
    handleSaveAdjustments(adjustments) {
      this.invoice.invoiceAdjustments = adjustments;
      this.invoice.adjustmentsTotal = adjustments.reduce((accumulator, currentValue) => accumulator + currentValue.amount, 0);
      this.$emit('save-adjustments', this.invoice);
    }
  }
};
</script>
<style scoped lang="scss">
.short-button {
    height: 25px;
}
.noWrap {
    white-space: nowrap;
}
:deep(.disabled ) {
    background: #edf1f2;
}
</style>
